import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Checkbox from '../../components/Checkbox'
import Input from '../../components/Input'
import { registerInitialValues, RegisterSchemaType, registerValidationSchema } from '../../forms/register'
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik'
import useApi from '../../hooks/use-api';
import { AppRoutes, Tags } from '../../utils/props';
import { documentTagNames } from '../../utils/documentSections';

export default function RegisterPage() {
  const { t } = useTranslation('ns');
  const navigate = useNavigate();
  const form = useFormik({
    initialValues: registerInitialValues,
    validationSchema: registerValidationSchema,
    onSubmit: (values) => {
      onRegister(values)
    },
  })

  const { errors, touched, getFieldProps, handleSubmit } = form


  const register = useApi({
    path: '/companies',
    defaultErrorMessage: t('register-incorrect-data'),
    callback: (data) => {
      localStorage.setItem('token', JSON.stringify(data.auth_token));
      localStorage.setItem('refresh_token', JSON.stringify(data.refresh_token));
      // After login, fetch user data
      user.get();
      // Seed all tags on registration so uploads can be tagged immediately.
      [Tags.ProfilePicture, ...documentTagNames].forEach((tagName) => {
        tags.post({
          name: String(tagName),
        });
      });
      navigate(AppRoutes.PersonalInformation);
    },
    errorCallback: (error) => {
      form.setErrors({email: errors.email || error?.response?.data?.message || '', password: errors.password})
    }
  })

  const tags = useApi({
    path: '/tags',
  })

  const user = useApi({
    path: '/user',
    callback: (data) => {
      localStorage.setItem('user_id', JSON.stringify(data.id));
      localStorage.setItem('first_name', JSON.stringify(data.first_name));
      localStorage.setItem('last_name', JSON.stringify(data.last_name));
      localStorage.setItem('company_name', JSON.stringify(data.company_name));
      localStorage.setItem('phone', JSON.stringify(data.phone));
      localStorage.setItem('mail', JSON.stringify(data.mail));
      localStorage.setItem('date_of_birth', JSON.stringify(data.date_of_birth));
      localStorage.setItem('type', JSON.stringify(data.type));
    }
  })

  const onRegister = async (
    values: RegisterSchemaType
  ) => {
    const { firstName, lastName, companyName, dob, email, password, phone } = values

    await register.post({
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      date_of_birth: dob,
      mail: email,
      key: password,
      phone: phone,
      auth_method: 0,
      device_type: 0,
      device_token: '',
      auth_token: '',
    })

  }

  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-cover bg-center"
      style={{
        background: `linear-gradient(180deg, #086CE1 0%, rgba(8, 108, 225, 0) 98%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 98%), url('/images/account-background.jpg') lightgray -2.651px -381.993px/100.239% 142.326% no-repeat`,
      }}
    >
      <div className="relative z-10 flex w-full flex-col items-center">
        <h1 className="mx-auto mb-12 text-white max-w-lg text-center text-3xl font-extrabold text-primary drop-shadow-lg md:text-4xl">
          {t('registerTitle', { defaultValue: 'Create an Account' })}
        </h1>
        <div className="w-full max-w-2xl px-4">
          <form className="flex flex-col items-center px-8 pb-8 pt-10" onSubmit={handleSubmit}>
            <div className="mb-6 grid w-full grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                {...getFieldProps('firstName')}
                title={t('firstName')}
                error={touched.firstName && errors.firstName ? errors.firstName : ''}
                labelClassName="text-white"
              />
              <Input
                {...getFieldProps('lastName')}
                title={t('lastName')}
                error={touched.lastName && errors.lastName ? errors.lastName : ''}
                labelClassName="text-white"
              />
            </div>
            <div className="mb-6 w-full">
              <Input
                {...getFieldProps('email')}
                title={t('email')}
                type="email"
                error={touched.email && errors.email ? errors.email : ''}
                labelClassName="text-white"
              />
            </div>
            <div className="mb-6 grid w-full grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                {...getFieldProps('password')}
                title={t('password')}
                type="password"
                error={touched.password && errors.password ? errors.password : ''}
                labelClassName="text-white"
              />
              <Input
                {...getFieldProps('confirmPassword')}
                title={t('confirmPassword')}
                type="password"
                error={
                  touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''
                }
                labelClassName="text-white"
              />
            </div>
            <button
              className="mb-6 w-full rounded bg-blue-600 py-3 text-lg font-bold text-white shadow-md transition hover:bg-blue-700"
              type="submit"
            >
              {t('createAccountButton', { defaultValue: 'CREATE ACCOUNT' })}
            </button>
            <div className="mt-2 w-full text-center text-base text-white">
              <span>{t('alreadyHaveAccount', { defaultValue: 'Already have an account? ' })}</span>
              <Link
                className="font-semibold text-blue-300 transition hover:text-blue-100 hover:underline"
                to={AppRoutes.Login}
              >
                {t('loginHere', { defaultValue: 'Login here' })}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}